import React, { useCallback, useEffect, useState } from "react";

const CHAINS = {
  ETH: { chainId: "0x1", chainName: "Ethereum Mainnet", nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 }, rpcUrls: ["https://cloudflare-eth.com"], blockExplorerUrls: ["https://etherscan.io"] },
  BSC: { chainId: "0x38", chainName: "BNB Smart Chain", nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 }, rpcUrls: ["https://bsc-dataseed.binance.org"], blockExplorerUrls: ["https://bscscan.com"] },
  POLYGON: { chainId: "0x89", chainName: "Polygon", nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 }, rpcUrls: ["https://polygon-rpc.com"], blockExplorerUrls: ["https://polygonscan.com"] },
  ARBITRUM: { chainId: "0xa4b1", chainName: "Arbitrum One", nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 }, rpcUrls: ["https://arb1.arbitrum.io/rpc"], blockExplorerUrls: ["https://arbiscan.io"] },
  OPTIMISM: { chainId: "0xa", chainName: "Optimism", nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 }, rpcUrls: ["https://mainnet.optimism.io"], blockExplorerUrls: ["https://optimistic.etherscan.io"] },
  AVAX: { chainId: "0xa86a", chainName: "Avalanche C-Chain", nativeCurrency: { name: "AVAX", symbol: "AVAX", decimals: 18 }, rpcUrls: ["https://api.avax.network/ext/bc/C/rpc"], blockExplorerUrls: ["https://snowtrace.io"] },
};

const ALIASES = { ERC20: "ETH", ETHEREUM: "ETH", BEP20: "BSC", BNB: "BSC", BSC: "BSC", BNBSMARTCHAIN: "BSC", MATIC: "POLYGON", POL: "POLYGON", ARB: "ARBITRUM", ARBITRUMONE: "ARBITRUM", OP: "OPTIMISM", AVAXC: "AVAX", AVALANCHE: "AVAX", AVALANCHECCHAIN: "AVAX" };

export const evmChainFor = (networkName) => {
  const key = String(networkName ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const code = ALIASES[key] ?? key;
  return CHAINS[code] ?? Object.values(CHAINS).find((chain) => chain.chainId === networkName) ?? null;
};

export const switchToNetwork = async (networkName) => {
  const chain = evmChainFor(networkName);
  if (!window.ethereum || !chain) return false;
  try {
    await window.ethereum.request({ method: "wallet_switchEthereumChain", params: [{ chainId: chain.chainId }] });
  } catch (error) {
    if (error?.code !== 4902) throw error;
    await window.ethereum.request({ method: "wallet_addEthereumChain", params: [chain] });
  }
  return true;
};

// Sends the native coin; token deposits need the user's address copy flow.
export const sendNative = async ({ from, to, amount }) => {
  const [whole, frac = ""] = String(amount).split(".");
  const wei = window.BigInt(`${whole || "0"}${frac.padEnd(18, "0").slice(0, 18)}`);
  return window.ethereum.request({ method: "eth_sendTransaction", params: [{ from, to, value: `0x${wei.toString(16)}` }] });
};

let pendingConnect = null;

export const useMetaMask = () => {
  const [account, setAccount] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!window.ethereum) return undefined;
    window.ethereum.request({ method: "eth_accounts" }).then((list) => setAccount(list?.[0] ?? "")).catch(() => {});
    const onChange = (list) => setAccount(list?.[0] ?? "");
    window.ethereum.on?.("accountsChanged", onChange);
    return () => window.ethereum.removeListener?.("accountsChanged", onChange);
  }, []);

  const connect = useCallback(async () => {
    setError("");
    if (!window.ethereum) {
      setError("MetaMask is not installed. Install the MetaMask extension or app to continue.");
      return "";
    }
    try {
      // MetaMask rejects a second request while one is pending, so share the in-flight one.
      if (!pendingConnect) {
        pendingConnect = window.ethereum.request({ method: "eth_requestAccounts" }).finally(() => { pendingConnect = null; });
      }
      const list = await pendingConnect;
      setAccount(list?.[0] ?? "");
      return list?.[0] ?? "";
    } catch (err) {
      setError(err?.code === -32002
        ? "A MetaMask request is already pending. Open the MetaMask extension and approve or reject it."
        : err?.message || "MetaMask connection was rejected.");
      return "";
    }
  }, []);

  return { account, error, setError, connect };
};

export const MetaMaskButton = ({ account, onConnect, label = "Connect MetaMask" }) => (
  <button
    type="button"
    onClick={onConnect}
    style={{ background: "#f6851b", color: "#fff", border: 0, borderRadius: 6, padding: "8px 14px", fontWeight: 600, cursor: "pointer" }}
  >
    {account ? `🦊 ${account.slice(0, 6)}…${account.slice(-4)}` : `🦊 ${label}`}
  </button>
);
